import { openProfileDocument } from "../../../backend/transport";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DetailBox, PaginatedTable, TitleBar, SearchBar } from '../../common';
import {
  TITLES,
  RECEIPT_INNER_COLUMN,
  ROUTES,
  BUTTON,
  POPUP_MSG,
  POPUP_TYPE,
  LOCAL_STORAGE,
  BLP_COLUMNS,
  DROPDOWN,
} from '../../../constants';
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../../backend/navigation";
import {
  receiptDetailBlpDataCompiler,
  receiptDetailCompiler,
  receiptDetailsBox,
} from '../../../utils/dataCompiler/receiptDataCompiler';
import { PopUpBox, Button, ThresholdWarningPopup } from '../../shared';
import { useLoader } from '../../../context/LoaderContext';
import {
  changeDispatchStatus,
  packageCreationDyeing,
  updateReceiptQty,
  validateDispatch,
  vendorReceipt,
  vendorReceiptMoveIds,
} from '../../../service';
import { getLocalStorageItem, popup } from '../../../utils';
import { printLabelPackage } from '../../../service/package';
import { useDebounce } from '../../../hooks';
import { MfgTable } from '../manufacturing/clothManufacturing/MfgTable';

export const ReceiptDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [popUp, setPopUp] = useState(false);
  const [validate, setValidate] = useState(false);
  const [popUpData, setPopUpData] = useState('');
  const [receiptData, setReceiptsData] = useState(state);
  const { setLoading } = useLoader();

  // Move IDs state
  const [moveIdsData, setMoveIdsData] = useState([]);
  const [moveIdsPaginationData, setMoveIdsPaginationData] = useState({});
  const [moveIdsPageNumber, setMoveIdsPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [isMoveIdsLoading, setIsMoveIdsLoading] = useState(false);

  const data = useMemo(
    () =>
      receiptDetailCompiler({
        moveIds: moveIdsData,
        state: receiptData?.state,
        data: receiptData,
      }),
    [moveIdsData, receiptData],
  );
  const canShowValidateButton = shouldShowValidateButton(data);
  const [isLoading, setIsLoading] = useState(false);
  const [isReceiptValidateLoading, setIsReceiptValidateLoading] = useState(false);
  const [activeUpdateRequests, setActiveUpdateRequests] = useState(0);
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const isUpdateLoading = activeUpdateRequests > 0;
  const blpData = useMemo(() => {
    if(moveIdsData.length){
      return receiptDetailBlpDataCompiler(moveIdsData);
    }
    return [];
  }, [moveIdsData])

  let column = RECEIPT_INNER_COLUMN.filter(
    (col) =>
      (state?.state === 'done' ? col.name !== 'Action' : true) &&
      (state?.vendor_id?.vendor_type !== 'fabric'
        ? col.name !== 'Demand Rolls' && col.name !== 'Received Rolls'
        : true) &&
      (state?.vendor_id?.vendor_type !== 'dyeing' ? col.name !== 'Shipped by ESUN' : true),
  );

  // Load move IDs data
  const fetchMoveIdsData = useCallback((additionalParams = {}) => {
    if (!receiptData?.id) return;

    setIsMoveIdsLoading(true);

    const params = {
      receipt_id: receiptData.id,
      page_number: additionalParams.page_number || moveIdsPageNumber,
      search: searchTerm
        ? {
            future_lot: searchTerm,
            'product_id.default_code': searchTerm,
            'product_id.name': searchTerm,
            'move_line_ids.lot_name': searchTerm,
          }
        : {},
      ...additionalParams,
    };

    vendorReceiptMoveIds({ data: params })
      .then((response) => {
        setIsMoveIdsLoading(false);
        if (response?.data?.records?.length > 0) {
          setMoveIdsData(response.data.records);
          setMoveIdsPaginationData(response.data.params);
        } else {
          setMoveIdsData([]);
        }
      })
      .catch((error) => {
        setIsMoveIdsLoading(false);
        console.error('Error fetching move IDs:', error);
        setMoveIdsData([]);
      });
  }, [receiptData?.id, moveIdsPageNumber, searchTerm]);

  const lastMovePage = useRef(null);
  useEffect(() => {
    if (!receiptData?.id || moveIdsPageNumber < 1) return;
    const key = `${receiptData.id}:${moveIdsPageNumber}`;
    if (lastMovePage.current === key) return;
    lastMovePage.current = key;
    fetchMoveIdsData({ page_number: moveIdsPageNumber });
  }, [receiptData?.id, moveIdsPageNumber, fetchMoveIdsData]);

  const previousSearch = useRef(false);
  useEffect(() => {
    const submitted = search && !previousSearch.current;
    previousSearch.current = search;
    if (submitted && receiptData?.id) {
      lastMovePage.current = `${receiptData.id}:1`;
      setMoveIdsPageNumber(1);
      fetchMoveIdsData({ page_number: 1 });
    }
  }, [search, receiptData?.id, fetchMoveIdsData]);

  const nilMoveLineCheck = (data) => {
    if (data?.length) {
      const allLinesWithZeroQty = data.every((move) => {
        const doneQty = move?.received_quantity || 0;
        return doneQty === 0;
      });
      return allLinesWithZeroQty;
    }
    return false;
  };

  const reloadData = useCallback((update = false) => {
    if (!update) {
      setIsLoading(true);
    }
    vendorReceipt({
      data: {
        receipt_ids: state?.id,
        page_number: 1,
      },
    })
      .then((response) => {
        // setLoading(false);
        if(update){
          fetchMoveIdsData();
        }
        setIsLoading(false);
        if (response.data.records.length > 0) {
          setReceiptsData(response.data.records[0]);
        } else setReceiptsData(response.data.message);
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
      })
      .catch(() => {
        setIsLoading(false);
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
      });
  }, [state?.id, fetchMoveIdsData]);

  const loadedRecord = useRef(null);
  useEffect(() => {
    if (!state?.id) {
      navigate(ROUTES.RECEIPT);
      return;
    }
    if (loadedRecord.current === state.id) return;
    loadedRecord.current = state.id;
    reloadData();
  }, [state?.id, navigate, reloadData]);

  const validateReceipt = () => {
    setIsReceiptValidateLoading(true);
    validateDispatch({ data: { picking_ids: state?.id } })
      .then((response) => {
        setIsReceiptValidateLoading(false);
        popup(POPUP_TYPE.SUCCESS, response.message);
        // navigate(ROUTES.RECEIPT)
        window.history.back();
      })
      .catch(() => {
        setIsReceiptValidateLoading(false);
      });
  };

  const updateReceipt = async (data, force_submit = false) => {
    // dispatch(setLoaderVisibility(true));
    setActiveUpdateRequests((prev) => prev + 1);
    
    try {
      const response = await updateReceiptQty({ 
        data: { 
          ...data,
          force_submit: force_submit
        } 
      });
      
      // Check if response is a threshold warning (produce or damage)
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        // Show threshold warning popup
        setThresholdWarningData(response);
        setShowThresholdWarning(true);
        setPendingUpdateData(data);
        setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
        return; // Wait for user confirmation
      }
      
      // Success - no warning
      if (response.data && response.data.length) {
        popup(POPUP_TYPE.SUCCESS, response.message);
      } else {
        popup(POPUP_TYPE.ERR, POPUP_MSG.INVALID_QTY_MSG);
      }
        reloadData(true);
    } catch (error) {
        setActiveUpdateRequests(0);
        setLoading(false);
        popup(POPUP_TYPE.ERR, error.message);
    }
  };

  const handleThresholdWarningConfirm = async (editedValues) => {
    setShowThresholdWarning(false);
    
    // Update quantities if edited
    const finalData = { ...pendingUpdateData };
    if (editedValues && editedValues.length > 0) {
      const editedProduce = editedValues.find(ev => ev.type === 'produce');
      const editedDamage = editedValues.find(ev => ev.type === 'damage');
      if (editedProduce) {
        finalData.done_qty = parseFloat(editedProduce.quantity);
      }
      if (editedDamage) {
        finalData.damage_qty = parseFloat(editedDamage.quantity);
      }
    }
    
    // Re-call API with force_submit=true
    await updateReceipt(finalData, true);
    
    setPendingUpdateData(null);
    setThresholdWarningData(null);
  };

  const handleThresholdWarningCancel = () => {
    setShowThresholdWarning(false);
    setPendingUpdateData(null);
    setThresholdWarningData(null);
    setActiveUpdateRequests((prev) => Math.max(prev - 1, 0));
  };

  const handlePrintLabel = useDebounce(() => {
    popup(POPUP_TYPE.SUCCESS, 'Label Printing Initiated');
    printLabelPackage({
      data: {
        sizes: [DROPDOWN.BOX_OPTION[0]],
        package_types: ['single'],
        package_ids: blpData.map((pkg) => pkg.id),
      },
    })
      .then((response) => {
        if (response.data.length > 0) {
          const url = response.data[0].split('?')[0];
          openProfileDocument(url, '_blank', 'noreferrer');
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, 1000);

  const handleDispatch = useDebounce(() => {
    changeDispatchStatus({
      data: { dispatch_id: state?.id },
    })
      .then((response) => {
        setLoading(false);
        popup('success', response.message);
        reloadData();
        // navigate(ROUTES.RECEIPT);
        window.history.back();
      })
      .catch(() => {
        setLoading(false);
      });
  }, 400);

  const createPackage = () => {
    setIsReceiptValidateLoading(true);
    packageCreationDyeing({ data: { picking_id: [state?.id] } })
      .then((response) => {
        setIsReceiptValidateLoading(true);
        popup(POPUP_TYPE.SUCCESS, response.message);
        reloadData(true);
      })
      .catch(() => {
        setIsReceiptValidateLoading(true);
      });
  };

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.RECEIPT, route: ROUTES.RECEIPT },
          { text: state?.name, route: ROUTES.NULL },
        ]}
      />
      <div className="scroll-div">
        <div className="d-flex justify-content-end gap-2 mb-3">
          {canShowValidateButton && state?.vendor_id?.vendor_type !== 'dyeing' && (
            <Button
              clickHandler={validateReceipt}
              disabled={isReceiptValidateLoading || nilMoveLineCheck(data)}
              className={`border-0 blue-btn d-flex ${nilMoveLineCheck(data) || isReceiptValidateLoading ? 'disabled' : ''} align-items-center justify-content-center py-2 px-3`}>
              {isReceiptValidateLoading && (
                <span className="spinner-border spinner-border-sm text-white me-2" role="status" />
              )}
              {BUTTON.VALIDATE}
            </Button>
          )}
          {getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' &&
            receiptData?.dispatch_status !== 'dispatched' &&
            receiptData?.dispatch_status !== 'received' && (
              <Button
                clickHandler={handleDispatch}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {BUTTON.DISPATCH}
              </Button>
            )}
          {receiptData?.vendor_id &&
            receiptData?.vendor_id?.vendor_type === 'dyeing' &&
            (receiptData?.vendor_id !== false && receiptData?.state !== 'done' ? (
              <Button
                clickHandler={createPackage}
                disabled={nilMoveLineCheck(data) || isReceiptValidateLoading}
                className={`border-0 blue-btn d-flex ${nilMoveLineCheck(data) || isReceiptValidateLoading ? 'disabled' : ''} align-items-center justify-content-center py-2 px-3`}>
                {isReceiptValidateLoading && (
                  <span
                    className="spinner-border spinner-border-sm text-white me-2"
                    role="status"
                  />
                )}
                {isReceiptValidateLoading ? 'Validating...' : BUTTON.CREATE_PACKAGE_AND_VALIDATE}
              </Button>
            ) : (
              <Button
                clickHandler={handlePrintLabel}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {BUTTON.PRINT_LABEL}
              </Button>
            ))}
        </div>
        <DetailBox colCount={2} colData={receiptData} dataCompiler={receiptDetailsBox} />

        {/* Search Section for Move IDs */}
        <div className="d-flex align-items-center justify-content-start gap-3 w-100 mb-3 mt-3">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={setSearch}
            onDefaultSearch={() => {
              lastMovePage.current = `${receiptData?.id}:1`;
              setMoveIdsPageNumber(1);
              fetchMoveIdsData({ page_number: 1, search: {} });
            }}
          />
        </div>

        {isUpdateLoading && (
          <span
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              gap: '8px',
            }}>
            <span className="spinner-border spinner-border-sm text-primary" role="status" />
            Updating...
          </span>
        )}
        <PaginatedTable
          columns={column}
          initialData={data}
          PAGE_SIZE={20}
          colFilter={false}
          popUp={popUp}
          setPopUp={setPopUp}
          setPopUpData={setPopUpData}
          paginationData={moveIdsPaginationData}
          setPageNumber={setMoveIdsPageNumber}
          isLoading={isMoveIdsLoading || isLoading}
        />
        {validate && (
          <PopUpBox openPopUpBox={validate}>
            <ReceiptValidate
              apiCall={validateReceipt}
              setOpenPopUpBox={setValidate}
              id={receiptData.id}
            />
          </PopUpBox>
        )}
        {popUp && (
          <PopUpBox openPopUpBox={popUp}>
            <QtyUpdate
              apiCall={updateReceipt}
              setOpenPopUpBox={setPopUp}
              data={popUpData}
              vendor_type={state?.vendor_id?.vendor_type}
            />
          </PopUpBox>
        )}
        {showThresholdWarning && thresholdWarningData && (
          <PopUpBox openPopUpBox={showThresholdWarning} modalWidth="50%">
            <ThresholdWarningPopup
              warningData={thresholdWarningData}
              onConfirm={handleThresholdWarningConfirm}
              onCancel={handleThresholdWarningCancel}
              allowEdit={true}
            />
          </PopUpBox>
        )}
        {receiptData?.vendor_id &&
          receiptData?.vendor_id?.vendor_type === 'dyeing' &&
          receiptData?.vendor_id !== false &&
          receiptData?.state === 'done' && (
            <div className="edit-container-rec p-2">
              <h5 className="text-center mb-4 mt-0">BLP Labels</h5>
              <div className="w-100 table-3">
                <MfgTable
                  column={BLP_COLUMNS}
                  data={blpData}
                />
              </div>
            </div>
          )}
      </div>
    </>
  );
};

const QtyUpdate = ({ apiCall, setOpenPopUpBox, data, vendor_type }) => {
  const [doneQty, setDoneQty] = useState(
    vendor_type === 'fabric' && data?.rolls ? data?.rolls || '' : data?.received_quantity || ''
  );
  const [damageQty, setDamageQty] = useState(data?.damaged_quantity || '');
  const qtyRef = useRef();
  const rollsQty = data?.rolls;

  const closePopUp = () => {
    setOpenPopUpBox(false);
  };

  const handleApiCall = () => {
    const finalDoneQty = doneQty === '' ? 0 : parseFloat(doneQty);
    const finalDamageQty = damageQty === '' ? 0 : parseFloat(damageQty);

    setOpenPopUpBox(false);
    const apiData = {
      move_id: data.id,
      done_qty: parseFloat(finalDoneQty),
      damage_qty: parseFloat(finalDamageQty),
      rolls: vendor_type === 'fabric' ? true : false,
    };
    apiCall(apiData);
  };

  const isUpdateDisabled = () => {
    const initialDoneQty = vendor_type === 'fabric' && data?.rolls ? (data?.rolls || '') : (data?.received_quantity || '');
    const initialDamageQty = data?.damaged_quantity || '';
    const isDoneQtyUnchanged = doneQty.toString() === initialDoneQty.toString();
    const isDamageQtyUnchanged = damageQty.toString() === initialDamageQty.toString();
    return isDoneQtyUnchanged && isDamageQtyUnchanged;
  };

  useEffect(() => {
    qtyRef.current.focus();
  }, []);

  return (
    <>
      <div className="d-flex flex-column m-1 ">
        <h4 className=" text-center text-special mb-3 mt-3 ">
          {vendor_type === 'fabric' && rollsQty ? 'Enter Rolls Quantity' : POPUP_MSG.ENTER_QTY}
        </h4>
        <hr />
        <div className="w-100 modal-body py-0 d-flex flex-column gap-3 justify-content-center align-items-center">
          <div className="d-flex justify-content-between w-100 ">
            <label className="ms-3">Quantity</label>
            <input
              type="number"
              className="ps-2 curve-border me-4"
              value={doneQty}
              ref={qtyRef}
              onWheel={(e) => e.target.blur()}
              step={vendor_type === 'fabric' ? '1' : 'any'}
              onKeyDown={(e) => {
                if (
                  vendor_type === 'fabric' &&
                  rollsQty &&
                  (e.key === '.' || e.key === 'Decimal')
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                setDoneQty(value);
                if (!value || parseFloat(value) === 0) {
                  setDamageQty('');
                }
              }}
              onPaste={(e) => {
                if (vendor_type === 'fabric' && rollsQty) {
                  const paste = e.clipboardData.getData('text');
                  if (paste.includes('.')) {
                    e.preventDefault();
                  }
                }
              }}
            />
          </div>
          <div className="d-flex justify-content-between w-100 ">
            <label className="ms-3">Dmg Qty.</label>
            <input
              type="number"
              className="ps-2 curve-border me-4"
              value={damageQty}
              disabled={!doneQty || parseFloat(doneQty) === 0}
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (
                  vendor_type === 'fabric' &&
                  rollsQty &&
                  (e.key === '.' || e.key === 'Decimal')
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                setDamageQty(e.target.value);
              }}
              onPaste={(e) => {
                if (vendor_type === 'fabric' && rollsQty) {
                  const paste = e.clipboardData.getData('text');
                  if (paste.includes('.')) {
                    e.preventDefault();
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4  modal-btn custom-button">
        <div className="ms-5 ps-5 w-50 d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleApiCall}
            disabled={isUpdateDisabled()}
            className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isUpdateDisabled() ? 'disabled' : ''}`}>
            {BUTTON.UPDATE}
          </Button>
          <Button
            clickHandler={closePopUp}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};

const ReceiptValidate = ({ apiCall, setOpenPopUpBox, id }) => {
  const closePopUp = () => {
    setOpenPopUpBox(false);
  };
  const handleApiCall = () => {
    setOpenPopUpBox(false);
    apiCall({
      picking_id: id,
    });
  };
  return (
    <>
      <div className="d-flex m-1 ">
        <div className="w-25 modal-body py-0 d-flex justify-content-center align-items-center">
          <span className="d-flex justify-content-center align-items-center bg-info badge">!</span>
        </div>
        <div className="w-75 modal-body py-0">
          <h4 className=" text-center text-special mb-3 "> {POPUP_MSG.SURE}</h4>
          <p className="text-center text-special placeholder-color ">{POPUP_MSG.QTY_WILL_SAVE}</p>
        </div>
      </div>
      <hr />
      <div className="pb-3 mt-2 d-flex align-items-center justify-content-end mx-4  modal-btn custom-button">
        <div className="ms-5 ps-5 w-50 d-flex align-items-center justify-content-between">
          <Button
            clickHandler={handleApiCall}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CONFIRM}
          </Button>{' '}
          <Button
            clickHandler={closePopUp}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      </div>
    </>
  );
};

const shouldShowValidateButton = (data) => {
  for (const item of data) {
    return item.state === 'Assigned' ? true : false;
  }
};


